// Master list of Danish cities targeted for local-SEO landing pages.
// Distance is approx. road-km from AutoXChange (Industrivej 127, 7080 Børkop).
// Used to seed `city_landing_pages` rows and to render the homepage SEO
// coverage grid even before pages are published.

export interface CitySeed {
  /** URL slug used in /bilforhandler-<slug>. Lowercase, ASCII, hyphenated. */
  slug: string;
  /** Display name with proper Danish casing/diacritics. */
  name: string;
  /** Approx. distance from Børkop in km — used in copy and ordering. */
  distanceKm: number;
}

export const CITY_SEEDS: CitySeed[] = [
  { slug: "aabenraa", name: "Aabenraa", distanceKm: 80 },
  { slug: "aarup", name: "Aarup", distanceKm: 35 },
  { slug: "agerskov", name: "Agerskov", distanceKm: 65 },
  { slug: "billund", name: "Billund", distanceKm: 40 },
  { slug: "bjert", name: "Bjert", distanceKm: 18 },
  { slug: "bramming", name: "Bramming", distanceKm: 70 },
  { slug: "bredsten", name: "Bredsten", distanceKm: 22 },
  { slug: "brejning", name: "Brejning", distanceKm: 6 },
  { slug: "brenderup", name: "Brenderup", distanceKm: 30 },
  { slug: "broerup", name: "Brørup", distanceKm: 55 },
  { slug: "christiansfeld", name: "Christiansfeld", distanceKm: 35 },
  { slug: "ejby", name: "Ejby", distanceKm: 28 },
  { slug: "erritsoe", name: "Erritsø", distanceKm: 12 },
  { slug: "esbjerg", name: "Esbjerg", distanceKm: 75 },
  { slug: "fjelsted", name: "Fjelsted", distanceKm: 32 },
  { slug: "fredericia", name: "Fredericia", distanceKm: 14 },
  { slug: "gelsted", name: "Gelsted", distanceKm: 30 },
  { slug: "gram", name: "Gram", distanceKm: 60 },
  { slug: "haderslev", name: "Haderslev", distanceKm: 45 },
  { slug: "harndrup", name: "Harndrup", distanceKm: 38 },
  { slug: "hedensted", name: "Hedensted", distanceKm: 25 },
  { slug: "holsted", name: "Holsted", distanceKm: 60 },
  { slug: "jelling", name: "Jelling", distanceKm: 22 },
  { slug: "jels", name: "Jels", distanceKm: 50 },
  { slug: "kolding", name: "Kolding", distanceKm: 22 },
  { slug: "lunderskov", name: "Lunderskov", distanceKm: 32 },
  { slug: "middelfart", name: "Middelfart", distanceKm: 20 },
  { slug: "moelholm", name: "Mølholm", distanceKm: 18 },
  { slug: "roedding", name: "Rødding", distanceKm: 55 },
  { slug: "roedekro", name: "Rødekro", distanceKm: 75 },
  { slug: "skodborg", name: "Skodborg", distanceKm: 50 },
  { slug: "sommersted", name: "Sommersted", distanceKm: 40 },
  { slug: "strib", name: "Strib", distanceKm: 22 },
  { slug: "taulov", name: "Taulov", distanceKm: 10 },
  { slug: "toftlund", name: "Toftlund", distanceKm: 65 },
  { slug: "vamdrup", name: "Vamdrup", distanceKm: 32 },
  { slug: "vejen", name: "Vejen", distanceKm: 50 },
  { slug: "vejle", name: "Vejle", distanceKm: 18 },
  { slug: "vinding", name: "Vinding", distanceKm: 16 },
  { slug: "vissenbjerg", name: "Vissenbjerg", distanceKm: 45 },
  { slug: "viuf", name: "Viuf", distanceKm: 20 },
  { slug: "vojens", name: "Vojens", distanceKm: 50 },
  { slug: "vonsild", name: "Vonsild", distanceKm: 25 },
];

/** Build the public landing-page URL for a city. */
export function cityLandingPath(citySlug: string): string {
  return `/bilforhandler-${citySlug}`;
}

/**
 * Returns the N cities geographically closest to the given city — used to
 * surface 2-3 neighbour-city links on each landing page so search engines
 * see a tight internal-linking silo (and so a real visitor browsing
 * "Bilforhandler Vejle" can hop to "Bilforhandler Fredericia" / "Kolding"
 * in one click). Closeness is approximated as `|distanceA - distanceB|`
 * since both distances are measured from the same dealer in Børkop —
 * close-to-each-other-from-Børkop is a good proxy for close-to-each-other.
 */
export function findNeighborCities(
  citySlug: string,
  count = 3,
): CitySeed[] {
  const self = CITY_SEEDS.find((c) => c.slug === citySlug);
  if (!self) return [];
  return CITY_SEEDS.filter((c) => c.slug !== citySlug)
    .map((c) => ({ city: c, gap: Math.abs(c.distanceKm - self.distanceKm) }))
    .sort((a, b) => a.gap - b.gap)
    .slice(0, count)
    .map((x) => x.city);
}
